/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from 'react';
import {
  // Alert,
  Box,
  Button,
  // Divider,
  Grid,
  Paper,
  Stack,
  // TextField,
  Typography,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { keyframes, useTheme } from '@mui/material/styles';
import { Google as GoogleIcon } from '@mui/icons-material';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import PolicyRoundedIcon from '@mui/icons-material/PolicyRounded';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { googleLogin, exchangeAuthCode } from '../../services/api';
import { getPostLoginRedirect } from '../../utils/routeAccess';
import { WELCOME_SESSION_STORAGE_KEY } from '../../components/WelcomeOverlay';
import { ENROLLMENT_WELCOME_SESSION_KEY } from '../../components/EnrollmentWelcomeDialog';
import InstitutionBrandRow from '../../components/InstitutionBrandRow';
import { useBranding } from '../../context/BrandingContext';
import { rounded } from '../../theme/tokens';
const pageFadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

/** Card shell: scale + opacity (wrapper; tilt lives on Paper) */
const cardEnter = keyframes`
  from {
    opacity: 0;
    transform: scale(0.98);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const CARD_DEPTH_TRANSITION = '0.2s ease';
const CARD_MAX_TILT_DEG = 3;
const CARD_PARALLAX_GAIN = 0.68;

/** Feature row icons — single size for visual consistency */
const FEATURE_ICON_SX = { fontSize: 20, display: 'block' };

const BRAND_FEATURES = [
  {
    icon: <CodeRoundedIcon sx={FEATURE_ICON_SX} />,
    text: 'Monitor coding progress across batches',
  },
  {
    icon: <SchoolRoundedIcon sx={FEATURE_ICON_SX} />,
    text: 'Manage courses and assessments seamlessly',
  },
  {
    icon: <PolicyRoundedIcon sx={FEATURE_ICON_SX} />,
    text: 'Detect plagiarism with insights',
  },
];

const LoginPage = () => {
  const theme = useTheme();
  const { productName, branding } = useBranding();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [oauthExchangeError, setOauthExchangeError] = useState('');
  const [oauthExchanging, setOauthExchanging] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const authCode = params.get('authCode');
    const oauthError = params.get('oauthError');

    if (oauthError === 'email_domain') {
      setOauthExchangeError(
        'Only institutional Google accounts (@bvrithyderabad.edu.in) can sign in to StackTrack. Personal Gmail accounts are not accepted here — you can still use them for LeetCode.',
      );
      return;
    }

    if (!authCode) return;

    let cancelled = false;

    const completeOAuthExchange = async () => {
      setOauthExchanging(true);
      setOauthExchangeError('');
      try {
        const data = await exchangeAuthCode(authCode);
        if (cancelled) return;

        if (!data?.token) {
          setOauthExchangeError('Sign-in could not be completed. Please try again.');
          return;
        }

        const { token, ...session } = data;
        login(session, token);
        try {
          sessionStorage.setItem(WELCOME_SESSION_STORAGE_KEY, '1');
          if (session.enrollmentWelcome?.newlyClaimed) {
            sessionStorage.setItem(
              ENROLLMENT_WELCOME_SESSION_KEY,
              JSON.stringify(session.enrollmentWelcome),
            );
          }
        } catch {
          /* ignore */
        }
        navigate(getPostLoginRedirect(session.role, location.state?.from?.pathname, session), {
          replace: true,
          state: { showWelcome: true },
        });
      } catch (error) {
        if (cancelled) return;
        const message =
          error?.response?.data?.message ||
          error?.message ||
          'Sign-in could not be completed. Please try again.';
        setOauthExchangeError(message);
      } finally {
        if (!cancelled) {
          setOauthExchanging(false);
        }
      }
    };

    completeOAuthExchange();

    return () => {
      cancelled = true;
    };
  }, [location.search, login, navigate, location.state?.from?.pathname]);

  useEffect(() => {
    if (!user || location.pathname !== '/login') return;
    const params = new URLSearchParams(location.search);
    if (params.get('authCode')) return;
    navigate(getPostLoginRedirect(user.role, location.state?.from?.pathname, user), { replace: true });
  }, [user, navigate, location.pathname, location.search, location.state]);

  const handleGoogleLogin = () => {
    googleLogin();
  };

  // const [pwUsername, setPwUsername] = useState('');
  // const [pwPassword, setPwPassword] = useState('');
  // const [pwFieldErrors, setPwFieldErrors] = useState({ username: '', password: '' });
  // const [pwSubmitError, setPwSubmitError] = useState('');
  // const [pwSubmitting, setPwSubmitting] = useState(false);

  // const validatePasswordForm = () => {
  //   const u = pwUsername.trim();
  //   const p = pwPassword;
  //   const next = { username: '', password: '' };
  //   if (!u) next.username = 'Username is required.';
  //   else if (u.length < 3) next.username = 'Use at least 3 characters.';
  //   else if (u.length > 128) next.username = 'Username is too long.';
  //   if (!p) next.password = 'Password is required.';
  //   else if (p.length < 8) next.password = 'Use at least 8 characters.';
  //   else if (p.length > 256) next.password = 'Password is too long.';
  //   setPwFieldErrors(next);
  //   return !next.username && !next.password;
  // };

  // const handlePasswordLogin = async (e) => {
  //   e.preventDefault();
  //   setPwSubmitError('');
  //   if (!validatePasswordForm()) return;
  //   setPwSubmitting(true);
  //   try {
  //     const data = await postPasswordLogin(pwUsername.trim(), pwPassword);
  //     if (!data?.token || !data?.user) {
  //       setPwSubmitError('Unexpected response from server.');
  //       return;
  //     }
  //     login(data.user, data.token);
  //     try {
  //       sessionStorage.setItem(WELCOME_SESSION_STORAGE_KEY, '1');
  //     } catch {
  //       /* ignore */
  //     }
  //     navigate(
  //       getPostLoginRedirect(data.user.role, location.state?.from?.pathname, data.user),
  //       { replace: true, state: { showWelcome: true } }
  //     );
  //   } catch (err) {
  //     const status = err?.response?.status;
  //     const body = err?.response?.data;
  //     if (status === 400 && body && typeof body === 'object') {
  //       const code = body.code;
  //       const msg = typeof body.message === 'string' ? body.message : 'Invalid request.';
  //       if (code === 'MISSING_CREDENTIALS' || code === 'INVALID_USERNAME') {
  //         setPwFieldErrors((prev) => ({ ...prev, username: msg }));
  //       } else if (code === 'PASSWORD_TOO_SHORT' || code === 'PASSWORD_TOO_LONG') {
  //         setPwFieldErrors((prev) => ({ ...prev, password: msg }));
  //       } else {
  //         setPwSubmitError(msg);
  //       }
  //     } else {
  //       setPwSubmitError(err?.response?.data?.message || err?.message || 'Sign-in failed.');
  //     }
  //   } finally {
  //     setPwSubmitting(false);
  //   }
  // };

  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)', { defaultMatches: false });

  const [cardHover, setCardHover] = useState(false);
  const [tilt, setTilt] = useState({ rx: 0, ry: 0 });

  const handleCardMouseMove = useCallback(
    (e) => {
      if (reduceMotion) return;
      const el = e.currentTarget;
      const r = el.getBoundingClientRect();
      const dx = (e.clientX - r.left) / r.width - 0.5;
      const dy = (e.clientY - r.top) / r.height - 0.5;
      const rawRx = -dy * 2 * CARD_MAX_TILT_DEG * CARD_PARALLAX_GAIN;
      const rawRy = dx * 2 * CARD_MAX_TILT_DEG * CARD_PARALLAX_GAIN;
      setTilt({
        rx: Math.min(CARD_MAX_TILT_DEG, Math.max(-CARD_MAX_TILT_DEG, rawRx)),
        ry: Math.min(CARD_MAX_TILT_DEG, Math.max(-CARD_MAX_TILT_DEG, rawRy)),
      });
    },
    [reduceMotion],
  );

  const handleCardMouseEnter = useCallback(() => setCardHover(true), []);
  const handleCardMouseLeave = useCallback(() => {
    setCardHover(false);
    setTilt({ rx: 0, ry: 0 });
  }, []);

  const liftPx = !reduceMotion && cardHover ? -2 : 0;
  const rx = reduceMotion ? 0 : tilt.rx;
  const ry = reduceMotion ? 0 : tilt.ry;

  const primaryMain = theme.palette.primary.main;
  const panel = theme.palette.ef?.panel ?? theme.palette.background.paper;
  const chrome = theme.palette.ef?.chrome ?? theme.palette.background.paper;
  const canvas = theme.palette.ef?.canvas ?? theme.palette.background.default;
  const edge = theme.palette.ef?.edge ?? theme.palette.divider;
  const hair = theme.palette.ef?.hair ?? theme.palette.divider;
  const textPrimary = theme.palette.text.primary;

  return (
    <Box
      component="main"
      sx={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        maxWidth: '100vw',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        display: 'flex',
        bgcolor: 'background.default',
        isolation: 'isolate',
        ...(!reduceMotion && {
          animation: `${pageFadeIn} 200ms ease-out both`,
        }),
      }}
    >
      <Grid
        container
        direction={{ xs: 'column', md: 'row' }}
        wrap="nowrap"
        sx={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          minHeight: '100vh',
          m: 0,
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Grid
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'center',
            alignSelf: 'stretch',
            minHeight: { md: '100vh' },
            width: '100%',
            boxSizing: 'border-box',
            px: { md: 6, lg: 8 },
            py: { md: 8 },
            bgcolor: chrome,
            position: 'relative',
            overflow: 'hidden',
            borderRight: `1px solid ${edge}`,
          }}
          size={{
            xs: 12,
            md: 7
          }}>
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'stretch',
                gap: 2,
                mb: 3,
              }}
            >
              <Box
                aria-hidden
                sx={{
                  width: 3,
                  flexShrink: 0,
                  borderRadius: `${rounded.sm}px`,
                  bgcolor: 'primary.main',
                  alignSelf: 'stretch',
                  minHeight: 48,
                }}
              />
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <InstitutionBrandRow
                  size="lg"
                  variant="h5"
                  logoHeight={44}
                  nameColor={textPrimary}
                  sx={{
                    alignItems: 'center',
                    '& .MuiTypography-root': { fontWeight: 500, lineHeight: 1.35 },
                  }}
                />
                {Boolean(branding.shortName?.trim()) &&
                  branding.name?.trim() &&
                  branding.name.trim() !== branding.shortName.trim() && (
                    <Typography
                      variant="body2"
                      component="p"
                      sx={{
                        mt: 2,
                        color: 'text.secondary',
                        fontWeight: 400,
                        letterSpacing: '0.01em',
                        lineHeight: 1.55,
                        maxWidth: 420,
                      }}
                    >
                      {branding.name}
                    </Typography>
                  )}
              </Box>
            </Box>

            <Typography
              variant="overline"
              component="p"
              sx={{
                color: 'text.secondary',
                mb: 4,
              }}
            >
              Track. Analyze. Improve.
            </Typography>

            <Stack spacing={4}>
              {BRAND_FEATURES.map(({ icon, text }) => (
                <Box
                  key={text}
                  sx={{
                    display: 'flex',
                    gap: 2,
                    alignItems: 'center',
                  }}
                >
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: 36,
                      height: 36,
                      borderRadius: `${rounded.md}px`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'action.hover',
                      border: `1px solid ${edge}`,
                      color: 'text.primary',
                      '& svg': { fontSize: 20, width: 20, height: 20 },
                    }}
                  >
                    {icon}
                  </Box>
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.6,
                      fontWeight: 400,
                      letterSpacing: '0.01em',
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {text}
                  </Typography>
                </Box>
              ))}
            </Stack>

            <Typography
              variant="caption"
              component="p"
              sx={{
                mt: 6,
                color: 'text.disabled',
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontWeight: 400,
                lineHeight: 1.5,
              }}
            >
              Powered by {productName}
            </Typography>
          </Box>
        </Grid>

        <Grid
          sx={{
            alignSelf: 'stretch',
            minHeight: { xs: 'auto', md: '100vh' },
            width: '100%',
            maxWidth: '100%',
            boxSizing: 'border-box',
            position: 'relative',
            overflow: 'hidden',
            bgcolor: canvas,
          }}
          size={{
            xs: 12,
            md: 5
          }}>
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'center', md: 'flex-start' },
              minHeight: { xs: 'auto', md: '100vh' },
              width: '100%',
              maxWidth: '100%',
              boxSizing: 'border-box',
              px: { xs: 2, sm: 4, md: 6 },
              py: { xs: 6, md: 8 },
            }}
          >
            <Box sx={{ maxWidth: 420, width: '100%', boxSizing: 'border-box' }}>
              <Box
                onMouseEnter={handleCardMouseEnter}
                onMouseMove={handleCardMouseMove}
                onMouseLeave={handleCardMouseLeave}
                sx={{
                  width: '100%',
                  borderRadius: `${rounded.lg}px`,
                  ...(!reduceMotion && {
                    animation: `${cardEnter} 0.4s ease-out both`,
                  }),
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: { xs: 4, sm: 6 },
                    borderRadius: `${rounded.lg}px`,
                    border: `1px solid ${edge}`,
                    bgcolor: panel,
                    backdropFilter: 'none',
                    WebkitBackdropFilter: 'none',
                    boxShadow: 'none',
                    transform: reduceMotion
                      ? 'none'
                      : `translateY(${liftPx}px) perspective(1200px) rotateX(${rx}deg) rotateY(${ry}deg)`,
                    transformOrigin: 'center center',
                    transformStyle: 'preserve-3d',
                    transition: reduceMotion
                      ? undefined
                      : `transform ${CARD_DEPTH_TRANSITION}, border-color ${CARD_DEPTH_TRANSITION}`,
                    willChange: reduceMotion ? undefined : 'transform',
                    '&:focus-within': {
                      borderColor: primaryMain,
                    },
                  }}
                >
                <Box
                  sx={{
                    pb: 2.25,
                    mb: 3.25,
                    borderBottom: `1px solid ${hair}`,
                  }}
                >
                  <InstitutionBrandRow
                    size="md"
                    variant="subtitle1"
                    logoHeight={36}
                    nameColor="text.primary"
                    sx={{
                      alignItems: 'center',
                      '& .MuiTypography-root': {
                        fontWeight: 500,
                        fontSize: '1rem',
                        lineHeight: 1.5,
                      },
                    }}
                  />
                </Box>

                <Typography
                  variant="h5"
                  component="h2"
                  fontWeight={500}
                  sx={{
                    letterSpacing: '-0.02em',
                    mb: 2,
                    lineHeight: 1.45,
                    fontSize: { xs: '1.25rem', sm: '1.3125rem' },
                  }}
                >
                  Welcome back
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    mb: 3.5,
                    fontWeight: 400,
                    opacity: 0.82,
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                  }}
                >
                  {oauthExchanging ? (
                    'Completing sign-in…'
                  ) : (
                    <>
                      Sign in with your BVRIT Hyderabad Google account
                      (@bvrithyderabad.edu.in).
                      <Box component="span" display="block" sx={{ mt: 1, opacity: 0.95 }}>
                        You may continue using your personal Google account for
                        LeetCode. They do not need to be the same account.
                      </Box>
                    </>
                  )}
                </Typography>

                {oauthExchangeError ? (
                  <Typography
                    variant="body2"
                    color="error"
                    sx={{ mb: 2, lineHeight: 1.5 }}
                  >
                    {oauthExchangeError}
                  </Typography>
                ) : null}

                <Button
                  fullWidth
                  variant="outlined"
                  size="large"
                  startIcon={<GoogleIcon sx={{ fontSize: 20 }} />}
                  onClick={handleGoogleLogin}
                  disabled={oauthExchanging}
                  sx={{
                    py: 1.5,
                    borderRadius: `${rounded.md}px`,
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '0.9375rem',
                    lineHeight: 1.55,
                    borderColor: edge,
                    color: 'text.primary',
                    bgcolor: panel,
                    boxShadow: 'none',
                    transition: (t) =>
                      t.transitions.create(['background-color', 'border-color'], {
                        duration: t.transitions.duration.short,
                      }),
                    '&:hover': {
                      borderColor: edge,
                      bgcolor: 'action.hover',
                      boxShadow: 'none',
                    },
                    '&.Mui-focusVisible': {
                      outline: 'none',
                      /* design-system-focus-ring */
                      boxShadow: `0 0 0 2px ${theme.palette.ef?.focusInner ?? canvas}, 0 0 0 4px ${theme.palette.ef?.focusOuter ?? textPrimary}`,
                    },
                  }}
                >
                  Continue with Google
                </Button>

                {/* <Divider sx={{ my: 3 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ px: 1 }}>
                    Or username & password
                  </Typography>
                </Divider>

                <Box component="form" onSubmit={handlePasswordLogin} noValidate>
                  {pwSubmitError ? (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPwSubmitError('')}>
                      {pwSubmitError}
                    </Alert>
                  ) : null}
                  <Stack spacing={2}>
                    <TextField
                      label="Username"
                      name="username"
                      autoComplete="username"
                      value={pwUsername}
                      onChange={(ev) => {
                        setPwUsername(ev.target.value);
                        if (pwFieldErrors.username) setPwFieldErrors((p) => ({ ...p, username: '' }));
                      }}
                      error={Boolean(pwFieldErrors.username)}
                      helperText={pwFieldErrors.username || undefined}
                      disabled={pwSubmitting}
                      size="medium"
                      fullWidth
                    />
                    <TextField
                      label="Password"
                      name="password"
                      type="password"
                      autoComplete="current-password"
                      value={pwPassword}
                      onChange={(ev) => {
                        setPwPassword(ev.target.value);
                        if (pwFieldErrors.password) setPwFieldErrors((p) => ({ ...p, password: '' }));
                      }}
                      error={Boolean(pwFieldErrors.password)}
                      helperText={pwFieldErrors.password || 'Minimum 8 characters'}
                      disabled={pwSubmitting}
                      size="medium"
                      fullWidth
                    />
                    <Button type="submit" variant="contained" size="large" disabled={pwSubmitting} fullWidth sx={{ py: 1.25, borderRadius: '12px', textTransform: 'none', fontWeight: 600 }}>
                      {pwSubmitting ? 'Signing in…' : 'Sign in'}
                    </Button>
                  </Stack>
                </Box> */}

                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2, lineHeight: 1.5 }}>
                  Tip: where Git history is scored, increase commit frequency and use clear messages so reviewers can
                  follow your progress.
                </Typography>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  textAlign="center"
                  sx={{
                    mt: 3,
                    letterSpacing: '0.01em',
                    opacity: 0.72,
                    fontSize: '0.75rem',
                    lineHeight: 1.5,
                    fontWeight: 400,
                  }}
                >
                  For students and faculty
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  textAlign="center"
                  sx={{
                    mt: 2,
                    opacity: 0.38,
                    letterSpacing: '0.05em',
                    textTransform: 'uppercase',
                    fontSize: '0.625rem',
                    lineHeight: 1.5,
                    fontWeight: 400,
                  }}
                >
                  Powered by {productName}
                </Typography>
                </Paper>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default LoginPage;
